// Centralized localStorage cache manager for 3-tier caching architecture
// Tier 1: Hospitable API (upstream, called by sync)
// Tier 2: MongoDB (stores pre-computed slots)
// Tier 3: localStorage (this module - instant display, survives refresh)

const CACHE_KEYS = {
  REPORT_SLOTS: 'report_slots_cache',
  PAYOUT_SLOTS: 'payout_slots_cache',
  CALENDAR_SLOTS: 'calendar_slots_cache',
  PRELOAD_STATUS: 'preload_status',
  LAST_SESSION: 'last_session_timestamp'
};

const CACHE_CONFIG = {
  REPORTS_MONTHS_WINDOW: 12,    // Keep +/- 12 months
  PAYOUTS_WEEKS_WINDOW: 8,      // Keep +/- 8 weeks
  TTL_FRESH: 60 * 60 * 1000,    // 1 hour - aligns with cron job
  TTL_STALE: 2 * 60 * 60 * 1000, // 2 hours before considering totally stale
  SESSION_GAP: 60 * 60 * 1000   // 1 hour - clear cache if session gap > 1 hour
};

// ============================================
// Date/Key Utilities
// ============================================

// Get month key in YYYY-MM format from offset
export const getMonthKey = (offset = 0) => {
  const today = new Date();
  const targetDate = new Date(today.getFullYear(), today.getMonth() + offset, 1);
  const year = targetDate.getFullYear();
  const month = String(targetDate.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

// Get month key from a Date object
export const getMonthKeyFromDate = (date) => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

// Get week key (Saturday start) in YYYY-MM-DD format from offset
export const getWeekKey = (offset = 0) => {
  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(startDate.getDate() + (offset * 7));

  // Adjust to Saturday
  while (startDate.getDay() !== 6) {
    startDate.setDate(startDate.getDate() - 1);
  }
  startDate.setHours(0, 0, 0, 0);

  return startDate.toISOString().split('T')[0];
};

// Get week start Date from offset
export const getWeekStart = (offset = 0) => {
  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(startDate.getDate() + (offset * 7));

  // Adjust to Saturday
  while (startDate.getDay() !== 6) {
    startDate.setDate(startDate.getDate() - 1);
  }
  startDate.setHours(0, 0, 0, 0);

  return startDate;
};

// ============================================
// Report Slots Cache
// ============================================

export const getReportSlots = (monthKey) => {
  try {
    const allCache = JSON.parse(localStorage.getItem(CACHE_KEYS.REPORT_SLOTS) || '{}');
    return allCache[monthKey] || null;
  } catch (e) {
    console.error('[CacheManager] Error reading report slots:', e);
    return null;
  }
};

export const setReportSlots = (monthKey, data) => {
  try {
    const allCache = JSON.parse(localStorage.getItem(CACHE_KEYS.REPORT_SLOTS) || '{}');
    allCache[monthKey] = {
      slots: data,
      timestamp: Date.now()
    };
    localStorage.setItem(CACHE_KEYS.REPORT_SLOTS, JSON.stringify(allCache));
  } catch (e) {
    console.error('[CacheManager] Error writing report slots:', e);
  }
};

export const updateReportSlot = (monthKey, slotId, updates) => {
  try {
    const allCache = JSON.parse(localStorage.getItem(CACHE_KEYS.REPORT_SLOTS) || '{}');
    if (allCache[monthKey] && allCache[monthKey].slots) {
      allCache[monthKey].slots = allCache[monthKey].slots.map(slot =>
        slot._id === slotId ? { ...slot, ...updates } : slot
      );
      allCache[monthKey].timestamp = Date.now();
      localStorage.setItem(CACHE_KEYS.REPORT_SLOTS, JSON.stringify(allCache));
    }
  } catch (e) {
    console.error('[CacheManager] Error updating report slot:', e);
  }
};

export const clearReportSlots = (monthKey) => {
  try {
    const allCache = JSON.parse(localStorage.getItem(CACHE_KEYS.REPORT_SLOTS) || '{}');
    if (monthKey) {
      delete allCache[monthKey];
    }
    localStorage.setItem(CACHE_KEYS.REPORT_SLOTS, JSON.stringify(allCache));
  } catch (e) {
    console.error('[CacheManager] Error clearing report slots:', e);
  }
};

// ============================================
// Payout Slots Cache
// ============================================

export const getPayoutSlots = (weekKey) => {
  try {
    const allCache = JSON.parse(localStorage.getItem(CACHE_KEYS.PAYOUT_SLOTS) || '{}');
    return allCache[weekKey] || null;
  } catch (e) {
    console.error('[CacheManager] Error reading payout slots:', e);
    return null;
  }
};

export const setPayoutSlots = (weekKey, data) => {
  try {
    const allCache = JSON.parse(localStorage.getItem(CACHE_KEYS.PAYOUT_SLOTS) || '{}');
    allCache[weekKey] = {
      cleaners: data.cleaners || [],
      owners: data.owners || [],
      timestamp: Date.now()
    };
    localStorage.setItem(CACHE_KEYS.PAYOUT_SLOTS, JSON.stringify(allCache));
  } catch (e) {
    console.error('[CacheManager] Error writing payout slots:', e);
  }
};

export const updatePayoutSlot = (weekKey, slotId, type, updates) => {
  try {
    const allCache = JSON.parse(localStorage.getItem(CACHE_KEYS.PAYOUT_SLOTS) || '{}');
    if (allCache[weekKey]) {
      const arrayKey = type === 'cleaner' ? 'cleaners' : 'owners';
      allCache[weekKey][arrayKey] = allCache[weekKey][arrayKey].map(slot =>
        slot._id === slotId ? { ...slot, ...updates } : slot
      );
      allCache[weekKey].timestamp = Date.now();
      localStorage.setItem(CACHE_KEYS.PAYOUT_SLOTS, JSON.stringify(allCache));
    }
  } catch (e) {
    console.error('[CacheManager] Error updating payout slot:', e);
  }
};

export const clearPayoutSlots = (weekKey) => {
  try {
    const allCache = JSON.parse(localStorage.getItem(CACHE_KEYS.PAYOUT_SLOTS) || '{}');
    if (weekKey) {
      delete allCache[weekKey];
    }
    localStorage.setItem(CACHE_KEYS.PAYOUT_SLOTS, JSON.stringify(allCache));
  } catch (e) {
    console.error('[CacheManager] Error clearing payout slots:', e);
  }
};

// ============================================
// TTL / Freshness Checking
// ============================================

export const getCacheAge = (timestamp) => {
  if (!timestamp) return Infinity;
  return Date.now() - timestamp;
};

export const isCacheFresh = (timestamp) => {
  return getCacheAge(timestamp) < CACHE_CONFIG.TTL_FRESH;
};

export const isCacheValid = (timestamp) => {
  return getCacheAge(timestamp) < CACHE_CONFIG.TTL_STALE;
};

// ============================================
// Preload Status Tracking
// ============================================

export const getPreloadStatus = () => {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEYS.PRELOAD_STATUS) || '{}');
  } catch (e) {
    return {};
  }
};

export const setPreloadStatus = (component, status) => {
  try {
    const current = getPreloadStatus();
    current[component] = { status, timestamp: Date.now() };
    localStorage.setItem(CACHE_KEYS.PRELOAD_STATUS, JSON.stringify(current));
  } catch (e) {
    console.error('[CacheManager] Error setting preload status:', e);
  }
};

// ============================================
// Rolling Window Pruning
// ============================================

export const pruneOldCache = () => {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  // Prune report slots outside ±12 month window
  try {
    const reportCache = JSON.parse(localStorage.getItem(CACHE_KEYS.REPORT_SLOTS) || '{}');
    let prunedReports = 0;

    Object.keys(reportCache).forEach(monthKey => {
      const [year, month] = monthKey.split('-').map(Number);
      const monthsAgo = (currentYear * 12 + currentMonth) - (year * 12 + (month - 1));

      if (Math.abs(monthsAgo) > CACHE_CONFIG.REPORTS_MONTHS_WINDOW) {
        delete reportCache[monthKey];
        prunedReports++;
      }
    });

    if (prunedReports > 0) {
      localStorage.setItem(CACHE_KEYS.REPORT_SLOTS, JSON.stringify(reportCache));
      console.log(`[CacheManager] Pruned ${prunedReports} report slots outside window`);
    }
  } catch (e) {
    console.error('[CacheManager] Error pruning report slots:', e);
  }

  // Prune payout slots outside ±8 week window
  try {
    const payoutCache = JSON.parse(localStorage.getItem(CACHE_KEYS.PAYOUT_SLOTS) || '{}');
    let prunedPayouts = 0;

    Object.keys(payoutCache).forEach(weekKey => {
      const weekDate = new Date(weekKey);
      const weeksAgo = Math.floor((now - weekDate) / (7 * 24 * 60 * 60 * 1000));

      if (Math.abs(weeksAgo) > CACHE_CONFIG.PAYOUTS_WEEKS_WINDOW) {
        delete payoutCache[weekKey];
        prunedPayouts++;
      }
    });

    if (prunedPayouts > 0) {
      localStorage.setItem(CACHE_KEYS.PAYOUT_SLOTS, JSON.stringify(payoutCache));
      console.log(`[CacheManager] Pruned ${prunedPayouts} payout slots outside window`);
    }
  } catch (e) {
    console.error('[CacheManager] Error pruning payout slots:', e);
  }
};

// ============================================
// Full Cache Operations
// ============================================

export const clearAllCaches = () => {
  localStorage.removeItem(CACHE_KEYS.REPORT_SLOTS);
  localStorage.removeItem(CACHE_KEYS.PAYOUT_SLOTS);
  localStorage.removeItem(CACHE_KEYS.CALENDAR_SLOTS);
  localStorage.removeItem(CACHE_KEYS.PRELOAD_STATUS);
  // Also clear the old bootstrap cache if it exists
  localStorage.removeItem('bootstrap_cache');
  localStorage.removeItem('calendar_cache');
  console.log('[CacheManager] All caches cleared');
};

export const getCacheStats = () => {
  const stats = {
    reportSlots: { count: 0, size: 0, keys: [] },
    payoutSlots: { count: 0, size: 0, keys: [] },
    totalSize: 0
  };

  try {
    const reportCache = localStorage.getItem(CACHE_KEYS.REPORT_SLOTS) || '{}';
    const reportData = JSON.parse(reportCache);
    stats.reportSlots.count = Object.keys(reportData).length;
    stats.reportSlots.size = reportCache.length;
    stats.reportSlots.keys = Object.keys(reportData);

    const payoutCache = localStorage.getItem(CACHE_KEYS.PAYOUT_SLOTS) || '{}';
    const payoutData = JSON.parse(payoutCache);
    stats.payoutSlots.count = Object.keys(payoutData).length;
    stats.payoutSlots.size = payoutCache.length;
    stats.payoutSlots.keys = Object.keys(payoutData);

    stats.totalSize = stats.reportSlots.size + stats.payoutSlots.size;
  } catch (e) {
    console.error('[CacheManager] Error getting stats:', e);
  }

  return stats;
};

// ============================================
// Session-based cache clearing
// ============================================

const checkSessionGap = () => {
  try {
    const lastSession = localStorage.getItem(CACHE_KEYS.LAST_SESSION);
    const now = Date.now();

    if (lastSession) {
      const gap = now - parseInt(lastSession, 10);

      // If more than 1 hour since last session, clear all caches
      if (gap > CACHE_CONFIG.SESSION_GAP) {
        console.log(`[CacheManager] Session gap detected (${Math.round(gap / 60000)} min). Clearing stale caches.`);
        clearAllCaches();
      }
    }

    // Update last session timestamp
    localStorage.setItem(CACHE_KEYS.LAST_SESSION, now.toString());
  } catch (e) {
    console.error('[CacheManager] Error checking session gap:', e);
  }
};

// ============================================
// Initialize on load
// ============================================

export const initializeCache = () => {
  checkSessionGap();
  pruneOldCache();
  console.log('[CacheManager] Initialized', getCacheStats());
};

// Export config for external use
export { CACHE_KEYS, CACHE_CONFIG };
